import React from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { ReactComponent as FilledStarIcon } from "../../../images/filled-star.svg";
import { ReactComponent as StrokedStarIcon } from "../../../images/stroked-star.svg";
import UserService from "../../../app/data/user/userService";
import { QUERY_KEYS } from "../../../app/data/common/queryKeys";
import { ERROR_MESSAGES } from "../../../app/data/common/errorMessages";

export interface PageMenuProps {
  pageId: string;
}

const userService = UserService.getInstance();

const MAX_PAGES = 10;

const PageMenu: React.FC<PageMenuProps> = ({
  pageId,
}) => {
  const { data: favorites, refetch } = useQuery({
    queryKey: [QUERY_KEYS.FAVORITES],
    queryFn: async () => {
      const response = await userService.getFavorites();
      return response.data;
    },
    enabled: false,
  });

  const addFavoriteMutation = useMutation({
    mutationFn: () => {
      return userService.addFavorite(pageId);
    },
    onSuccess: () => {
      refetch();
    },
    onError: () => {
      toast.error(ERROR_MESSAGES.COMMON);
    }
  });

  const removeFavoriteMutation = useMutation({
    mutationFn: () => {
      return userService.removeFavorite(pageId);
    },
    onSuccess: () => {
      refetch();
    },
    onError: () => {
      toast.error(ERROR_MESSAGES.COMMON);
    }
  });

  const addFavorite = () => {
    if (favorites?.length && favorites.length >= MAX_PAGES) {
      toast.error(`You can not add more than ${MAX_PAGES} pages to your favorites.`);
      return;
    }
    addFavoriteMutation.mutate();
  }

  const isFavorite = !!favorites?.some(page => page.favoritePageId === pageId);

  const handleToggleFavorite = () => {
    if (isFavorite) {
      removeFavoriteMutation.mutate();
    } else {
      addFavorite();
    }
  };

  return (
    <button
      type="button"
      onClick={handleToggleFavorite}
      style={{
        background: "transparent",
        border: "none",
        cursor: "pointer",
        padding: 0,
        marginTop: 1,
      }}
      title={isFavorite ? "Remove from favorites" : "Add to favorites"}
      aria-pressed={isFavorite}
    >
      {isFavorite ? (
        <FilledStarIcon
          style={{ width: 24, height: 24 }}
          aria-hidden="true"
          focusable="false"
        />
      ) : (
        <StrokedStarIcon
          style={{ width: 24, height: 24 }}
          aria-hidden="true"
          focusable="false"
        />
      )}
    </button>
  )
};

export default PageMenu;
